import axios from 'axios';
import { refreshTokenApi } from '../../features/auth/api/auth';
import { useAuthStore } from '../../features/auth/store/authStore';
import { coordinatedTokenRefresh } from '../../features/auth/utils/authSessionCoordinator';
import { invalidateAuthSession } from '../../features/auth/utils/invalidateAuthSession';
import { AuthCookies } from '../../features/auth/utils/authCookieConstants';
import { resolveCsrfToken } from '../../features/auth/utils/csrf';
import { ApiError } from './error';
import { useSupportViewStore } from '../../features/host/store/supportViewStore';

// In dev use /api (Vite proxy); in prod set VITE_API_URL to full API origin (e.g. https://api.example.com)
const baseURL =
  (import.meta.env.VITE_API_URL as string | undefined) ??
  (import.meta.env.DEV ? '/api' : '');

export const apiClient = axios.create({
  baseURL,
  withCredentials: true,
});

apiClient.interceptors.request.use((config) => {
  const { token, csrfToken } = useAuthStore.getState();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const csrf = resolveCsrfToken(csrfToken);
  if (csrf) {
    config.headers[AuthCookies.csrfHeaderName] = csrf;
  }

  return config;
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  failedQueue = [];
}

function redirectToLogin() {
  void invalidateAuthSession();
  if (typeof window === 'undefined') {
    return;
  }

  const loginHash = '#/login';
  if (window.location.hash === loginHash) {
    return;
  }

  // Hash-only navigation avoids reloading index.html (which re-triggers bootstrap refresh).
  window.location.hash = loginHash;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._isRetry &&
      !originalRequest._skipAuthRefresh
    ) {
      const isImpersonating = useAuthStore.getState().user?.isImpersonating;

      if (isImpersonating) {
        useSupportViewStore.getState().clear();
        return Promise.reject(ApiError.fromAxiosError(error));
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._isRetry = true;
      isRefreshing = true;

      const { token: accessToken, csrfToken } = useAuthStore.getState();
      const csrf = resolveCsrfToken(csrfToken);

      if (!csrf) {
        redirectToLogin();
        return Promise.reject(error);
      }

      try {
        const result = await coordinatedTokenRefresh(() =>
          refreshTokenApi({
            accessToken: accessToken ?? null,
            csrfToken: csrf,
          })
        );

        if (result.success) {
          processQueue(null, result.accessToken);
          originalRequest.headers.Authorization = `Bearer ${result.accessToken}`;
          return apiClient(originalRequest);
        }

        processQueue(new Error('Token refresh failed'), null);
        redirectToLogin();
        return Promise.reject(ApiError.fromAxiosError(error));
      } catch (refreshError) {
        processQueue(refreshError, null);
        redirectToLogin();
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);
